import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarRange, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "react-toastify";
import { downloadSpreadsheet } from "../../utils/excelSheet";
import "../../CSS/Global.css";
import "./AdmissionExport.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const formatDate = (d) => {
    if (!d) return "";
    try {
        return new Date(d).toISOString().split("T")[0];
    } catch {
        return String(d);
    }
};

const ExportAdmission = () => {
    const navigate = useNavigate();
    const [isSpecificDateSelected, setIsSpecificDateSelected] = useState(false);
    const [fileFormat, setFileFormat] = useState("xlsx");
    const [admissionRows, setAdmissionRows] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const url = API_BASE_URL;
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || "";

    useEffect(() => {
        if (!isLoggedIn?.main_id) return;

        const headers = {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        fetch(`${url}/admission/alladmin/${isLoggedIn.main_id}`, { headers })
            .then((res) => res.json())
            .then((data) => {
                const rows = Array.isArray(data.user) ? data.user : [];
                setAdmissionRows(rows);
            })
            .catch((err) => console.error("Failed to fetch admissions:", err));
    }, [isLoggedIn?.main_id, url, token]);

    const handleScopeChange = (specific) => {
        setIsSpecificDateSelected(specific);
    };

    const handleExport = async () => {
        if (!fileFormat) {
            toast.error("Please select a file format.", { position: "top-center" });
            return;
        }
        if (admissionRows.length === 0) {
            toast.error("No admission data to export.", { position: "top-center" });
            return;
        }

        let filteredData = [...admissionRows];

        if (isSpecificDateSelected) {
            if (!startDate || !endDate) {
                toast.warn("Select both start and end dates.", { position: "top-center" });
                return;
            }

            filteredData = admissionRows.filter((row) => {
                const date = new Date(row.createdAt);
                const start = new Date(startDate);
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                return date >= start && date <= end;
            });

            if (filteredData.length === 0) {
                toast.info("No admissions in that date range.", { position: "top-center" });
                return;
            }
        }

        const cleanedData = filteredData.map((r) => ({
            "Admission ID": r.admission_id || "",
            "Payment ID": r.payment_id || "",
            Name: r.name || "",
            "Father Name": r.fatherName || "",
            "Mother Name": r.motherName || "",
            "Date of Birth": formatDate(r.dob),
            Aadhar: r.aadhar || "",
            Gender: r.gender || "",
            School: r.school || "",
            Address: r.address || "",
            Pincode: r.pincode || "",
            Mobile: r.mobile || "",
            Email: r.email || "",
            Center: r.center || "",
            "Batch ID": r.batch_id != null && r.batch_id !== "" ? String(r.batch_id) : "",
            "Batch Name": r.batch?.name || "",
            Status: r.status || "",
            "Rules Agreed": r.rulesAgreed || "",
            "Created At": formatDate(r.createdAt),
        }));

        const baseName = `admissions_${Date.now()}`;
        const fmt = (fileFormat || "xlsx").toLowerCase();

        try {
            if (fmt === "csv") {
                await downloadSpreadsheet(cleanedData, "Admissions", baseName, "csv");
            } else {
                await downloadSpreadsheet(cleanedData, "Admissions", baseName, "xlsx");
            }
            toast.success(`Exported ${cleanedData.length} row(s).`, { position: "top-center", autoClose: 2200 });
        } catch (e) {
            console.error(e);
            toast.error("Export failed. Try again.", { position: "top-center" });
        }
    };

    const count = admissionRows.length;

    return (
        <div className="admission-export-page">
            <div className="admission-export-page__bg" aria-hidden />
            <div className="admission-export-page__inner">
                <header className="admission-export-header">
                    <button type="button" className="admission-export-back" onClick={() => navigate("/admission")}>
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Admissions
                    </button>
                    <div className="admission-export-header-text">
                        <h1>Export admissions</h1>
                        <p>
                            Download a spreadsheet matching the import template. Filter by created date or export
                            everything.
                        </p>
                    </div>
                </header>

                <div className="admission-export-meta" role="status">
                    <FileSpreadsheet size={18} strokeWidth={2.25} aria-hidden />
                    {count === 0 ? "No records loaded yet" : `${count} admission${count === 1 ? "" : "s"} ready to export`}
                </div>

                <div className="admission-export-card">
                    <section className="admission-export-section" aria-labelledby="adm-ex-scope">
                        <h2 id="adm-ex-scope" className="admission-export-section__title">
                            <CalendarRange size={16} strokeWidth={2.25} aria-hidden />
                            Date scope
                        </h2>
                        <div className="admission-export-scope-grid">
                            <div className="admission-export-scope-option">
                                <input
                                    type="radio"
                                    name="exportOption"
                                    id="adm-ex-all"
                                    checked={!isSpecificDateSelected}
                                    onChange={() => handleScopeChange(false)}
                                />
                                <label htmlFor="adm-ex-all" className="admission-export-scope-card">
                                    <strong>All admissions</strong>
                                    <span>Every row loaded from your tenant</span>
                                </label>
                            </div>
                            <div className="admission-export-scope-option">
                                <input
                                    type="radio"
                                    name="exportOption"
                                    id="adm-ex-range"
                                    checked={isSpecificDateSelected}
                                    onChange={() => handleScopeChange(true)}
                                />
                                <label htmlFor="adm-ex-range" className="admission-export-scope-card">
                                    <strong>Date range</strong>
                                    <span>Filter by when the record was created</span>
                                </label>
                            </div>
                        </div>

                        {isSpecificDateSelected && (
                            <div className="admission-export-dates">
                                <div className="admission-export-field">
                                    <label htmlFor="adm-ex-start">Start date</label>
                                    <input
                                        id="adm-ex-start"
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="admission-export-field">
                                    <label htmlFor="adm-ex-end">End date</label>
                                    <input
                                        id="adm-ex-end"
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </section>

                    <section className="admission-export-section" aria-labelledby="adm-ex-format">
                        <h2 id="adm-ex-format" className="admission-export-section__title">
                            <Download size={16} strokeWidth={2.25} aria-hidden />
                            File format
                        </h2>
                        <div className="admission-export-formats" role="radiogroup" aria-label="Export format">
                            {[
                                { id: "csv", label: "CSV" },
                                { id: "xlsx", label: "Excel" },
                                { id: "xls", label: "Excel (legacy)" },
                            ].map(({ id, label }) => (
                                <div className="admission-export-format" key={id}>
                                    <input
                                        type="radio"
                                        name="fileFormat"
                                        id={`adm-fmt-${id}`}
                                        value={id}
                                        checked={fileFormat === id}
                                        onChange={(e) => setFileFormat(e.target.value)}
                                    />
                                    <label htmlFor={`adm-fmt-${id}`}>{label}</label>
                                </div>
                            ))}
                        </div>
                        <p className="admission-export-hint">
                            XLS and XLSX both download a modern <strong>.xlsx</strong> workbook (same as import sample).
                        </p>
                    </section>
                </div>

                <div className="admission-export-actions">
                    <button type="button" className="admission-export-btn admission-export-btn--secondary" onClick={() => navigate(-1)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="admission-export-btn admission-export-btn--primary"
                        onClick={handleExport}
                        disabled={count === 0}
                    >
                        <Download size={18} strokeWidth={2.25} aria-hidden />
                        Export
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportAdmission;
